/**
 * Apply the shared compact-card three-line budget. A two-line primary title
 * leaves one line for the secondary title; a one-line primary leaves two.
 * Callers own observation because their mount lifecycles differ.
 */
export function applyCompactTextBudget(card: HTMLElement, title: HTMLElement, twoLineClass: string): void {
	if (!card.isConnected) return;
	const view = card.ownerDocument.defaultView;
	const style = view?.getComputedStyle(title);
	const lineHeight = Number.parseFloat(style?.lineHeight ?? '');
	const fontSize = Number.parseFloat(style?.fontSize ?? '');
	const oneLine = Number.isFinite(lineHeight) ? lineHeight : Number.isFinite(fontSize) ? fontSize * 1.25 : 0;
	if (oneLine <= 0) return;
	card.classList.toggle(twoLineClass, title.getBoundingClientRect().height > oneLine * 1.5);
}

/**
 * Keep a compact-card title budget correct through the first font/layout
 * settlement as well as later responsive reflows. A title observer matters
 * here: the card/list can keep its own box while a late font changes only the
 * title from one line to two.
 */
export function observeCompactTextBudget(card: HTMLElement, title: HTMLElement, twoLineClass: string): () => void {
	const view = card.ownerDocument.defaultView;
	const sync = () => applyCompactTextBudget(card, title, twoLineClass);
	const settle = () => {
		if (!card.isConnected || typeof view?.requestAnimationFrame !== 'function') return;
		view.requestAnimationFrame(() => view.requestAnimationFrame(sync));
	};

	sync();
	settle();
	void card.ownerDocument.fonts?.ready.then(settle).catch(() => undefined);

	const Observer = view?.ResizeObserver;
	if (!Observer) return () => undefined;
	const observer = new Observer(() => {
		if (!card.isConnected) {
			observer.disconnect();
			return;
		}
		sync();
	});
	observer.observe(title);
	return () => observer.disconnect();
}
